/**
 * "My Week" — personalised home for each user.
 * RBAC-aware: only shows entries the user can see per their effective scope.
 */
import { useEffect, useState } from "react";
import { Briefcase, Gavel, CheckSquare, MessageSquare, Receipt, Calendar, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import { caseDisplayNumber, caseIdsLabel } from "@/lib/case";
import { fmtDate, fmtDateTime, fmtDateShort } from "@/lib/time";

export default function MyWeek() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/my/week").then(r => setData(r.data)).catch(e => toast.error(apiError(e))).finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="text-center text-muted-foreground text-sm py-12" data-testid="my-week-loading">
            <Loader2 className="w-4 h-4 inline animate-spin mr-2"/>Loading your week…
        </div>
    );
    if (!data) return null;

    const weekLabel = data.week_start && data.week_end
        ? `${fmtDateShort(data.week_start)} – ${fmtDate(data.week_end)}`
        : null;

    return (
        <div className="space-y-6" data-testid="my-week-page">
            <PageHeader
                title={<span className="inline-flex items-center gap-3"><Calendar className="w-7 h-7 text-primary" strokeWidth={1.5}/> My Week</span>}
                subtitle={weekLabel
                    ? `Showing hearings, tasks and invoices for ${weekLabel} only. Scoped to your personal access level.`
                    : "Everything assigned to you or created by you this week. Scoped to your personal access level."
                }
            />

            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Tile icon={Briefcase}     label="Open cases this week" value={data.open_cases.length}        testid="kpi-cases"/>
                <Tile icon={Gavel}         label="Hearings this week"   value={data.upcoming_hearings.length} testid="kpi-hearings"/>
                <Tile icon={CheckSquare}   label="Tasks due this week"  value={data.pending_tasks.length}     testid="kpi-tasks"/>
                <Tile icon={MessageSquare} label="Unread chat"          value={data.unread_chat_messages}     testid="kpi-chat"/>
                <Tile icon={Receipt}       label="Invoices this week"   value={data.recent_invoices.length}   testid="kpi-invoices"/>
            </div>

            {/* Lists */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Open cases this week" empty="No active cases with hearings or tasks this week" data-testid="section-cases">
                    {data.open_cases.map(c => {
                        const meta = [c.court, c.status].filter(Boolean).join(" · ");
                        const idsLabel = caseIdsLabel(c);
                        return (
                            <Row key={c.id} to={`/app/cases/${c.id}`} data-testid={`row-case-${c.id}`}>
                                <div className="text-sm text-foreground">{c.title || c.case_title || caseDisplayNumber(c)}</div>
                                {idsLabel && idsLabel !== "—" && (
                                    <div className="text-[11px] text-primary/80 font-mono mt-0.5"
                                         data-testid={`row-case-ids-${c.id}`}>{idsLabel}</div>
                                )}
                                <div className="text-[11px] text-muted-foreground mt-0.5">{meta || "—"}</div>
                            </Row>
                        );
                    })}
                </Section>

                <Section title="Hearings this week" empty="No hearings scheduled this week">
                    {data.upcoming_hearings.map(h => {
                        const idsLabel = caseIdsLabel(h);
                        return (
                            <Row key={h.id} to={`/app/hearings?open=${h.id}`} data-testid={`row-hearing-${h.id}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm text-foreground">
                                        {h.case_title ? h.case_title : (h.purpose || "Hearing")}
                                    </div>
                                    <div className="text-[11px] text-primary font-mono whitespace-nowrap">{h.scheduled_at ? fmtDateTime(h.scheduled_at) : "—"}</div>
                                </div>
                                {h.case_title && h.purpose && (
                                    <div className="text-[11px] text-foreground/70 mt-0.5">{h.purpose}</div>
                                )}
                                {idsLabel && idsLabel !== "—" && (
                                    <div className="text-[11px] text-primary/80 font-mono mt-0.5"
                                         data-testid={`row-hearing-ids-${h.id}`}>{idsLabel}</div>
                                )}
                                <div className="text-[11px] text-muted-foreground mt-0.5">{h.court || "—"}</div>
                            </Row>
                        );
                    })}
                </Section>

                <Section title="Tasks due this week" empty="No tasks due this week — all caught up!">
                    {data.pending_tasks.map(t => (
                        <Row key={t.id} to={`/app/tasks?open=${t.id}`} data-testid={`row-task-${t.id}`}>
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">{t.title || "Task"}</div>
                                <div className="text-[11px] text-muted-foreground font-mono">{t.due_date ? fmtDate(t.due_date) : ""}</div>
                            </div>
                            {t.description && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{t.description}</div>}
                        </Row>
                    ))}
                </Section>

                <Section title="Invoices raised this week" empty="No invoices raised this week">
                    {data.recent_invoices.map(i => (
                        <Row key={i.id} to={`/app/invoices?open=${i.id}`} data-testid={`row-invoice-${i.id}`}>
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">{i.number || "Invoice"}</div>
                                <div className="text-[11px] text-primary font-mono">₹{Number(i.amount || 0).toLocaleString("en-IN")}</div>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{i.status || "—"}</div>
                        </Row>
                    ))}
                </Section>
            </div>
        </div>
    );
}

function Tile({ icon: Icon, label, value, testid }) {
    return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-lawos-sm hover:border-primary/40 transition-colors" data-testid={testid}>
            <div className="flex items-center gap-2 text-muted-foreground">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5}/>
                <span className="text-[10px] uppercase tracking-[0.15em] font-medium">{label}</span>
            </div>
            <div className="text-3xl text-foreground mt-2 font-light font-heading">{value}</div>
        </div>
    );
}

function Section({ title, empty, children, ...rest }) {
    const hasChildren = Array.isArray(children) ? children.filter(Boolean).length > 0 : !!children;
    return (
        <section className="bg-card border border-border rounded-lg overflow-hidden shadow-lawos-sm" {...rest}>
            <header className="px-5 py-3 border-b border-border text-[11px] uppercase tracking-[0.15em] text-muted-foreground bg-muted/50 font-medium">{title}</header>
            <div className="divide-y divide-border">
                {hasChildren ? children : <div className="text-xs text-muted-foreground text-center py-8 italic">{empty}</div>}
            </div>
        </section>
    );
}

function Row({ children, to, ...rest }) {
    const inner = <div className="px-5 py-3 hover:bg-muted/50 transition-colors" {...rest}>{children}</div>;
    return to ? <Link to={to} className="block">{inner}</Link> : inner;
}
